import type { DBEquipmentBookingLine, DBEquipmentItem } from "@/lib/db-types";
import { addDays, availableQuantity, bookingConflicts, eachDay, type BookingLine } from "./availability";

/** En linje i bookingdialogen før den er lagret (VIB-2919). */
export interface DraftLine {
  /** Lokal nøkkel. Lik `id` for linjer som finnes fra før. */
  key: string;
  id?: string;
  item_id: string;
  quantity: number;
  date_from: string;
  date_to: string;
  note: string;
}

export interface DraftWarning {
  /** Andre bookinger som overlapper på dager der vi eier for lite. */
  conflicts: DBEquipmentBookingLine[];
  /** Ledig antall i linjens periode, uten denne bookingen. */
  available: number;
}

export const lineLabel = (l: Pick<DBEquipmentBookingLine, "project_code" | "project_name" | "title">): string =>
  l.project_code ? `${l.project_code} ${l.project_name ?? ""}`.trim() : (l.title ?? "Internt");

/** Utstyr som kan velges i dialogen: tilgjengelig og bookbart (enheten overstyrer kategorien). */
export function isBookable(item: DBEquipmentItem, categoryBookable: Map<string, boolean>): boolean {
  return item.status === "ok" && (item.bookable ?? categoryBookable.get(item.category_id) ?? true);
}

/** Vises i bookingsøket: bookbart utstyr, også det som er på service eller savnet (kan ikke velges). */
export function isSearchableForBooking(item: DBEquipmentItem, categoryBookable: Map<string, boolean>): boolean {
  return isBookable({ ...item, status: "ok" }, categoryBookable) && (item.status === "ok" || unavailableReason(item) !== null);
}

/**
 * Hvorfor utstyret ikke kan velges i bookingdialogen (VIB-2921), eller null om
 * det kan. Speiler validate_equipment_booking_item, som avviser det samme.
 */
export function unavailableReason(item: DBEquipmentItem): string | null {
  switch (item.status) {
    case "ok":
      return null;
    case "in_service":
      return "På service, kan ikke bookes før servicesaken er ferdig";
    case "missing":
      return "Savnet, kan ikke bookes";
    default:
      return null;
  }
}

/**
 * Advarsler per linje mot de andre aktive bookingene (`existing` inneholder
 * også linjene til bookingen som redigeres; de filtreres bort på booking_id).
 */
export function draftWarnings(params: {
  drafts: DraftLine[];
  bookingId: string | null;
  existing: DBEquipmentBookingLine[];
  items: Map<string, Pick<DBEquipmentItem, "quantity">>;
}): Map<string, DraftWarning> {
  const { drafts, bookingId, items } = params;
  const others = params.existing.filter((l) => l.booking_id !== bookingId);
  const otherById = new Map(others.map((l) => [l.id, l]));
  const draftLines: BookingLine[] = drafts.map((d) => ({
    id: d.key,
    booking_id: "draft",
    item_id: d.item_id,
    quantity: d.quantity,
    date_from: d.date_from,
    date_to: d.date_to,
  }));
  const owned = new Map([...items].map(([id, i]) => [id, i.quantity]));
  const conflicts = bookingConflicts([...others, ...draftLines], owned);

  const result = new Map<string, DraftWarning>();
  for (const d of drafts) {
    const item = items.get(d.item_id);
    const conflicting = conflicts
      .filter((c) => c.lineId === d.key && otherById.has(c.otherLineId))
      .map((c) => otherById.get(c.otherLineId)!);
    result.set(d.key, {
      conflicts: [...new Map(conflicting.map((l) => [l.id, l])).values()],
      available: item && d.date_from <= d.date_to
        ? availableQuantity(item.quantity, others, d.item_id, { from: d.date_from, to: d.date_to })
        : 0,
    });
  }
  return result;
}

/** Feilmelding før lagring, eller null når utkastet kan sendes. */
export function validateDraft(draft: { projectId: string | null; title: string; lines: DraftLine[] }): string | null {
  if (!draft.projectId && !draft.title.trim()) return "Velg et prosjekt, eller skriv en tittel for intern booking";
  if (draft.lines.length === 0) return "Legg til minst ett utstyr";
  if (draft.lines.some((l) => !l.date_from || !l.date_to)) return "Alle linjer må ha fra- og til-dato";
  if (draft.lines.some((l) => l.date_to < l.date_from)) return "Til-datoen kan ikke være før fra-datoen";
  if (draft.lines.some((l) => !Number.isInteger(l.quantity) || l.quantity < 1)) return "Antall må være minst 1";
  return null;
}

/** Antall dager fra `from` til `to` (negativt når `to` er før). */
export function daysBetween(from: string, to: string): number {
  return to >= from ? eachDay(from, to).length - 1 : -(eachDay(to, from).length - 1);
}

/**
 * «Kopier booking» (VIB-2920): linjene blir nye (uten id), med nye nøkler, og
 * flyttes like mange dager så innbyrdes datoer beholdes.
 */
export function copyLines(lines: DraftLine[], shiftDays: number, newKey: () => string): DraftLine[] {
  return lines.map((l) => ({
    key: newKey(),
    item_id: l.item_id,
    quantity: l.quantity,
    date_from: addDays(l.date_from, shiftDays),
    date_to: addDays(l.date_to, shiftDays),
    note: l.note,
  }));
}
